// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { InputMask } from '@tectonics/components/input-mask';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import {
  useUserInputValidationError,
  type UserInputValidationError,
  serverErrorMessage,
} from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import type { Country } from '@tectonics/prs/country';

import { Field } from '../../../molecules';
import { Input, Empty, Label } from '../../../atoms';

export function FieldCardExpiryDate(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const [value, handleChange] = useUserInputAsTextField('cardExpiryDate');

  const labelMessage = useLabelMessage();
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardExpiryDate');

  return (
    <Field
      size="half"
      data-test-name="card-expiry-date"
      label={<Label htmlFor={inputId}>{labelMessage}</Label>}
      showFloatingLabel={!!value}
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={validationError && formHasBeenSent}
    >
      <Input
        as={InputMask}
        data-secret
        mask={EXPIRY_DATE_MASK}
        id={inputId}
        onChange={handleChange}
        placeholder={labelMessage}
        readOnly={submitting}
        type="tel"
        value={value}
        name="card-expiry-date"
      />
    </Field>
  );
}

function useLabelMessage(): string {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Validade do cartão';
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'pe':
      return 'Fecha de Expiración';
    case 'bo':
    case 'ec':
    case 'uy':
    case 'py':
      return 'Fecha de Vencimiento';
    case 'za':
      return 'Due date';
    default:
      throw new Error(`No message found for field expiry date in country ${country}`);
  }
}

type ValidationErrorMessageProps = {|
  validationError: ?UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  if (!validationError) {
    return <Empty />;
  }

  const message = getErrorMessages(validationError)[country];

  if (message) {
    return message;
  }

  return serverErrorMessage(validationError.payload);
}

function getErrorMessages({ id }: UserInputValidationError): { [Country]: string } {
  switch (id) {
    case 'empty':
      return {
        br: 'Por favor, insira a validade do cartão.',
        mx: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        ar: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        co: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        cl: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        pe: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        ec: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        bo: 'Por favor, ingresa la fecha de vencimiento de tu tarjeta.',
        za: 'Please enter the expiration date of the card',
      };

    case 'expired':
      return {
        br: 'Ops! O cartão informado está vencido.',
        mx: '¡Ups! La tarjeta que informaste ha expirado. ',
        ar: '¡Ups! La tarjeta que informaste ha expirado. ',
        co: '¡Ups! La tarjeta que informaste ha expirado. ',
        cl: '¡Ups! La tarjeta que informaste ha expirado. ',
        pe: '¡Ups! La tarjeta que informaste ha expirado. ',
        ec: '¡Ups! La tarjeta que informaste ha expirado. ',
        bo: '¡Ups! La tarjeta que informaste ha expirado. ',
        za: 'Oops! The card you entered has expired',
      };

    default:
      return {
        br: 'A validade está incorreta.',
        mx: 'La fecha de vencimiento no es válida.',
        ar: 'La fecha de vencimiento no es válida.',
        co: 'La fecha de vencimiento no es válida.',
        cl: 'La fecha de vencimiento no es válida.',
        pe: 'La fecha de vencimiento no es válida.',
        ec: 'La fecha de vencimiento no es válida.',
        bo: 'La fecha de vencimiento no es válida.',
        za: 'Expiration date is invalid',
      };
  }
}

const EXPIRY_DATE_MASK = [/[0-9]/, /[0-9]/, '/', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
